/**
 * ConfigTemplatesPage — Sprint 4.6 (+ Sprint UX-BR-3 i18n).
 *
 * Tela principal de templates. Lista + ações (Novo / Editar / Duplicar /
 * Aplicar / Excluir / Ver histórico).
 */
import { useEffect, useState } from "react";
import {
  deleteConfigTemplate,
  listConfigTemplates,
} from "../../lib/api.js";
import RunHistoryDrawer from "./RunHistoryDrawer.jsx";
import TemplateApplyModal from "./TemplateApplyModal.jsx";
import TemplateBuilder from "./TemplateBuilder.jsx";
import { t } from "../../i18n/index.js";
import {
  filterTemplatesByKind,
  isInstallerPreset,
} from "../../lib/installerTemplates.js";

const SCOPE_COLORS = {
  wifi:       "bg-noc-accent/10 text-noc-accent border-noc-accent/30",
  network:    "bg-noc-purple/10 text-noc-purple border-noc-purple/30",
  management: "bg-noc-purple/10 text-noc-purple border-noc-purple/30",
  watchdog:   "bg-noc-warn/10 text-noc-warn border-noc-warn/30",
  schedule:   "bg-noc-ok/10 text-noc-ok border-noc-ok/30",
  advanced:   "bg-noc-accent/10 text-noc-accent border-noc-accent/30",
  mixed:      "bg-noc-purple/10 text-noc-purple border-noc-purple/30",
};


function fmtRel(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s atrás`;
  if (s < 3600) return `${Math.floor(s / 60)}min atrás`;
  if (s < 86400) return `${Math.floor(s / 3600)}h atrás`;
  return d.toLocaleDateString("pt-BR");
}

export default function ConfigTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [scopeFilter, setScopeFilter] = useState("");
  // INSTALLER-TEMPLATES-NOC-UX-1 — filtro client-side por tipo de template.
  const [kindFilter, setKindFilter] = useState("all"); // all | installer | common

  // Modais
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editingTpl, setEditingTpl] = useState(null);  // null=novo
  const [applyOpen, setApplyOpen] = useState(false);
  const [applyingTpl, setApplyingTpl] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyTplId, setHistoryTplId] = useState(null);

  const reload = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await listConfigTemplates({ scope: scopeFilter || undefined });
      setTemplates(r.templates || []);
    } catch (e) {
      // Sprint 4.6.1 — defensivo contra [object Object]
      const detail = e?.data?.detail;
      let msg;
      if (typeof detail === "string") msg = detail;
      else if (e?.message) msg = String(e.message);
      else msg = t("templatesPage.errLoad");
      setErr(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [scopeFilter]);

  const onNew = () => { setEditingTpl(null); setBuilderOpen(true); };
  const onEdit = (tpl) => { setEditingTpl(tpl); setBuilderOpen(true); };
  const onDuplicate = (tpl) => {
    // TEMPLATES-NOC-2-PRESET-GUARD — cópia de preset vira template COMUM:
    // remove o prefixo reservado "[Installer]" (o backend rejeita o prefixo
    // fora dos presets) e o created_by (a cópia não é linha do sistema).
    const baseName = isInstallerPreset(tpl)
      ? tpl.name.replace(/^\[installer\]\s*/i, "")
      : tpl.name;
    setEditingTpl({
      ...tpl,
      id: undefined,
      created_by: undefined,
      name: `${baseName} ${t("templatesPage.duplicateSuffix")}`,
    });
    setBuilderOpen(true);
  };
  const onApply = (tpl) => { setApplyingTpl(tpl); setApplyOpen(true); };
  const onHistory = (tpl) => { setHistoryTplId(tpl?.id || null); setHistoryOpen(true); };
  const onDelete = async (tpl) => {
    if (!confirm(t("templatesPage.confirmDelete", { name: tpl.name })))
      return;
    try {
      await deleteConfigTemplate(tpl.id);
      await reload();
    } catch (e) {
      alert(t("templatesPage.errDelete") + (e?.message || "?"));
    }
  };

  // INSTALLER-TEMPLATES-NOC-UX-1 — filtra a lista já carregada por tipo.
  const installerCount = templates.filter(isInstallerPreset).length;
  const visible = filterTemplatesByKind(templates, kindFilter);

  return (
    <div className="min-h-full bg-noc-bg text-noc-text">
      <div className="border-b border-noc-border bg-noc-panel/50 px-4 py-3">
        <div className="flex items-center justify-between gap-2">
          <div>
            <h1 className="text-base font-semibold text-noc-accent">
              {t("templatesPage.title")}
            </h1>
            <div className="text-[11px] text-noc-muted mt-0.5">
              {t("templatesPage.subtitle")}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* INSTALLER-TEMPLATES-NOC-UX-1 — filtro por tipo de template */}
            <div className="flex rounded border border-noc-border overflow-hidden text-xs">
              {[
                ["all", "Todos"],
                ["installer", `Templates de Instalação${installerCount ? ` (${installerCount})` : ""}`],
                ["common", "Templates comuns"],
              ].map(([k, label]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setKindFilter(k)}
                  className={`px-2.5 py-1 ${
                    kindFilter === k
                      ? "bg-noc-accent/20 text-noc-accent font-semibold"
                      : "text-noc-muted hover:text-noc-text"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <select value={scopeFilter} onChange={(e) => setScopeFilter(e.target.value)}
              className="bg-noc-bg border border-noc-border rounded px-2 py-1 text-xs text-noc-text">
              <option value="">{t("templatesPage.allScopes")}</option>
              <option value="wifi">wifi</option>
              <option value="network">network</option>
              <option value="management">management</option>
              <option value="watchdog">watchdog</option>
              <option value="schedule">schedule</option>
              <option value="advanced">advanced</option>
              <option value="mixed">mixed</option>
            </select>
            <button type="button" onClick={() => onHistory(null)}
              className="px-2.5 py-1 text-xs rounded border border-noc-border text-noc-muted hover:text-noc-text">
              {t("templatesPage.generalHistory")}
            </button>
            <button type="button" onClick={onNew}
              className="px-3 py-1.5 text-xs rounded bg-noc-accent hover:bg-noc-glow text-noc-bg font-semibold font-semibold">
              {t("templatesPage.newTemplate")}
            </button>
          </div>
        </div>
      </div>

      <div className="p-4">
        {err && (
          <div className="text-xs text-noc-err bg-noc-err/10 border border-noc-err/30 rounded p-2 mb-3">
            {err}
          </div>
        )}

        {loading ? (
          <div className="text-xs text-noc-muted">{t("common.loading")}</div>
        ) : visible.length === 0 ? (
          <div className="text-center py-12 text-noc-muted">
            <div className="text-2xl mb-2">📄</div>
            <div className="text-sm">{t("templatesPage.empty")}</div>
            <button type="button" onClick={onNew}
              className="mt-3 px-3 py-1.5 text-xs rounded bg-noc-accent hover:bg-noc-glow text-noc-bg font-semibold">
              {t("templatesPage.emptyAction")}
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase text-noc-muted border-b border-noc-border">
                <tr>
                  <th className="text-left py-2 px-2">{t("templatesPage.colName")}</th>
                  <th className="text-left py-2 px-2">{t("templatesPage.colScope")}</th>
                  <th className="text-left py-2 px-2">{t("templatesPage.colTypes")}</th>
                  <th className="text-left py-2 px-2">{t("templatesPage.colUpdated")}</th>
                  <th className="text-left py-2 px-2">{t("templatesPage.colStatus")}</th>
                  <th className="text-right py-2 px-2">{t("templatesPage.colActions")}</th>
                </tr>
              </thead>
              <tbody>
                {visible.map(tpl => {
                  const types = Object.keys(tpl.payload_json || {});
                  const installer = isInstallerPreset(tpl);
                  return (
                    <tr key={tpl.id} className="border-b border-noc-border/40 hover:bg-noc-panel/40">
                      <td className="py-2 px-2">
                        <div className="font-semibold text-noc-accent flex items-center gap-1.5 flex-wrap">
                          {tpl.name}
                          {/* INSTALLER-TEMPLATES-NOC-UX-1 — badge + explicação */}
                          {installer && (
                            <span
                              title="Este template é usado pelo InstallerPortal durante o vínculo inicial do equipamento."
                              className="text-[9px] font-bold px-1.5 py-0.5 rounded border border-noc-accent/40 bg-noc-accent/10 text-noc-accent"
                            >
                              🏷 Template de Instalação
                            </span>
                          )}
                        </div>
                        {installer ? (
                          <div className="text-[10px] text-noc-accent/80 mt-0.5 max-w-md">
                            Usado pelo InstallerPortal no vínculo inicial do equipamento.
                            Alterações afetam <b>novas instalações</b> — não mexem em
                            equipamentos já instalados.
                          </div>
                        ) : (
                          tpl.description && (
                            <div className="text-[10px] text-noc-muted mt-0.5 max-w-xs truncate">
                              {tpl.description}
                            </div>
                          )
                        )}
                      </td>
                      <td className="py-2 px-2">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${SCOPE_COLORS[tpl.scope] || ""}`}>
                          {tpl.scope}
                        </span>
                      </td>
                      <td className="py-2 px-2 font-mono text-[10px] text-noc-muted">
                        {types.map(x => x.replace("config_", "")).join(", ")}
                      </td>
                      <td className="py-2 px-2 text-[10px] text-noc-muted">
                        {fmtRel(tpl.updated_at)}
                      </td>
                      <td className="py-2 px-2">
                        {tpl.enabled ? (
                          <span className="text-[10px] text-noc-ok">{t("templatesPage.enabledOn")}</span>
                        ) : (
                          <span className="text-[10px] text-noc-muted">{t("templatesPage.enabledOff")}</span>
                        )}
                      </td>
                      <td className="py-2 px-2 text-right whitespace-nowrap">
                        <button onClick={() => onApply(tpl)} title={t("templatesPage.titleApply")}
                          className="px-2 py-1 text-[10px] rounded bg-noc-accent hover:bg-noc-glow text-noc-bg font-semibold mr-1">
                          {t("templatesPage.btnApply")}
                        </button>
                        <button onClick={() => onHistory(tpl)} title={t("templatesPage.titleHistory")}
                          className="px-1.5 py-1 text-[10px] rounded border border-noc-border text-noc-muted hover:text-noc-text mr-1">
                          📜
                        </button>
                        <button onClick={() => onEdit(tpl)} title={t("templatesPage.titleEdit")}
                          className="px-1.5 py-1 text-[10px] rounded border border-noc-border text-noc-muted hover:text-noc-text mr-1">
                          ✏
                        </button>
                        <button onClick={() => onDuplicate(tpl)} title={t("templatesPage.titleDuplicate")}
                          className="px-1.5 py-1 text-[10px] rounded border border-noc-border text-noc-muted hover:text-noc-text mr-1">
                          📋
                        </button>
                        <button onClick={() => onDelete(tpl)} title={t("templatesPage.titleDelete")}
                          className="px-1.5 py-1 text-[10px] rounded border border-noc-err/30 text-noc-err hover:bg-noc-err/10">
                          🗑
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modais */}
      <TemplateBuilder
        open={builderOpen}
        template={editingTpl}
        onClose={() => setBuilderOpen(false)}
        onSaved={() => reload()}
      />
      <TemplateApplyModal
        open={applyOpen}
        template={applyingTpl}
        onClose={() => setApplyOpen(false)}
        onApplied={() => reload()}
      />
      <RunHistoryDrawer
        open={historyOpen}
        templateId={historyTplId}
        onClose={() => setHistoryOpen(false)}
      />
    </div>
  );
}
